import { supabase } from './supabase';

// ---- Institution CRUD ----

export interface InstitutionRow {
  id: string;
  user_id: string;
  name: string;
  type: string | null;
  city: string | null;
  district: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  foundation_year: number | null;
  description: string | null;
  services: string[] | null;
  departments: string[] | null;
  certificates: string[] | null;
  tech_devices: string[] | null;
  working_hours: Record<string, { open: string; close: string; closed: boolean }> | null;
  logo_url: string | null;
  cover_image_url: string | null;
  ivf_description: string | null;
  video_link: string | null;
  blog_link: string | null;
  video_links: Array<{ id: string; title: string; url: string }> | null;
  blog_links: Array<{ id: string; title: string; url: string }> | null;
  is_approved: boolean;
  is_pro_member: boolean;
  profile_updated_at: string | null;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export const institutionService = {
  /** Get institution by user_id (owner) */
  async getByUserId(userId: string) {
    const { data, error } = await supabase
      .from('institutions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    return { data: data as InstitutionRow | null, error };
  },

  /** Get institution by user_id - full data (owner, RLS uyumlu) */
  async getByUserIdFull(userId: string) {
    const { data, error } = await supabase
      .from('institutions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    return { data: data as InstitutionRow | null, error };
  },

  /** Get all approved institutions (for public listing) */
  async getApprovedInstitutions() {
    const { data, error } = await supabase
      .from('institutions')
      .select('*')
      .eq('is_approved', true)
      .eq('is_published', true)
      .neq('status', 'deleted')
      .neq('status', 'suspended')
      .order('created_at', { ascending: false });
    
    if (error) return { data: null, error };
    
    const validData = (data || []).filter((inst: any) => 
      inst.is_approved === true && 
      inst.is_published === true && 
      inst.status !== 'deleted' && 
      inst.status !== 'suspended'
    );
    
    return { data: validData as InstitutionRow[] | null, error: null };
  },

  /** Get institution by id - önce auth user ile dener, olmazsa public okur */
  async getById(id: string) {
    // Önce oturum açık kullanıcının kurumu mu diye bak (RLS uyumlu)
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: ownData, error: ownErr } = await supabase
        .from('institutions')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (ownData) return { data: ownData as InstitutionRow, error: null };
      if (ownErr) console.warn('getById owner query error:', ownErr);
    }
    // Public (onaylı) kurumlar için fallback
    const { data, error } = await supabase
      .from('institutions')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    return { data: data as InstitutionRow | null, error };
  },

  /** Create a new institution */
  async create(payload: Partial<InstitutionRow>) {
    const { data, error } = await supabase
      .from('institutions')
      .insert(payload)
      .select()
      .maybeSingle();
    return { data: data as InstitutionRow | null, error };
  },

  /** Update institution - RLS uyumlu */
  async update(id: string, payload: Partial<InstitutionRow>) {
    const { data, error } = await supabase
      .from('institutions')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data: data as InstitutionRow | null, error };
  },

  /** Get institution gallery */
  async getGallery(institutionId: string) {
    const { data, error } = await supabase
      .from('institution_gallery')
      .select('*')
      .eq('institution_id', institutionId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  /** Add gallery item */
  async addGalleryItem(institutionId: string, fileUrl: string, fileType: string, caption?: string) {
    const { data, error } = await supabase
      .from('institution_gallery')
      .insert({ institution_id: institutionId, file_url: fileUrl, file_type: fileType, caption: caption || null })
      .select()
      .maybeSingle();
    return { data, error };
  },

  /** Delete gallery item */
  async deleteGalleryItem(id: string) {
    const { error } = await supabase
      .from('institution_gallery')
      .delete()
      .eq('id', id);
    return { error };
  },

  /** Get doctors for institution */
  async getDoctors(institutionId: string) {
    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .eq('institution_id', institutionId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    return { data, error };
  },

  /** Add doctor */
  async addDoctor(payload: {
    institution_id: string;
    name: string;
    title?: string;
    specialization?: string;
    specialty_category?: string;
    experience_years?: number;
    education?: string;
    photo_url?: string;
    bio?: string;
    cv_file_url?: string;
    sort_order?: number;
  }) {
    const { data, error } = await supabase
      .from('doctors')
      .insert(payload)
      .select()
      .maybeSingle();
    return { data, error };
  },

  /** Update doctor */
  async updateDoctor(id: string, payload: Partial<{
    name: string;
    title: string;
    specialization: string;
    specialty_category: string;
    experience_years: number;
    education: string;
    photo_url: string;
    bio: string;
    cv_file_url: string;
    sort_order: number;
  }>) {
    const { data, error } = await supabase
      .from('doctors')
      .update(payload)
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data, error };
  },

  /** Delete doctor */
  async deleteDoctor(id: string) {
    const { error } = await supabase
      .from('doctors')
      .delete()
      .eq('id', id);
    return { error };
  },
};

// ---- Consultation Service ----

export interface ConsultationRow {
  id: string;
  institution_id: string;
  user_id: string | null;
  name: string;
  email: string;
  phone: string;
  message: string | null;
  status: string;
  note: string | null;
  created_at: string;
}

export const consultationService = {
  /** Create a new consultation request */
  async create(payload: {
    institution_id: string;
    user_id?: string | null;
    name: string;
    email: string;
    phone: string;
    message?: string;
  }) {
    const { data, error } = await supabase
      .from('consultations')
      .insert({
        ...payload,
        status: 'Beklemede',
      })
      .select()
      .maybeSingle();
    return { data: data as ConsultationRow | null, error };
  },

  /** Get consultations for a specific institution (owner view) */
  async getByInstitutionId(institutionId: string) {
    const { data, error } = await supabase
      .from('consultations')
      .select('*')
      .eq('institution_id', institutionId)
      .order('created_at', { ascending: false });
    return { data: data as ConsultationRow[] | null, error };
  },

  /** Get consultations for a specific user */
  async getByUserId(userId: string) {
    const { data, error } = await supabase
      .from('consultations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data: data as ConsultationRow[] | null, error };
  },

  /** Update consultation status */
  async updateStatus(id: string, status: string) {
    const { data, error } = await supabase
      .from('consultations')
      .update({ status })
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data: data as ConsultationRow | null, error };
  },
};

// ---- Storage helpers ----

export const institutionStorage = {
  /** Upload a file to institutions bucket */
  async uploadFile(userId: string, file: File, folder: string = 'general') {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `${userId}/${folder}/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;

    const { data, error } = await supabase.storage
      .from('institutions')
      .upload(fileName, file, { cacheControl: '3600', upsert: false });

    if (error) return { url: null, error };

    const { data: urlData } = supabase.storage
      .from('institutions')
      .getPublicUrl(data.path);

    return { url: urlData.publicUrl, error: null };
  },

  /** Delete a file from institutions bucket */
  async deleteFile(filePath: string) {
    // Extract path from full URL
    const bucketUrl = supabase.storage.from('institutions').getPublicUrl('').data.publicUrl;
    const relativePath = filePath.replace(bucketUrl, '').replace(/^\//, '');

    const { error } = await supabase.storage
      .from('institutions')
      .remove([relativePath]);
    return { error };
  },
};
